import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { ReactComponent as DefaultTest } from 'assets/icons/testKits/testKit.svg';
import EditQuantityCell from 'components/Shop/EditableCell';
import Select from 'react-select';
import { Option } from 'react-multi-select-component';
import ButtonComponent from 'components/button/buttonComponent';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import {
  ProviderRequestStatus,
  useGetProvidersByPatientIdQuery,
} from 'graphql/generated/remote-schema-hasura';
import { PROVIDERS } from 'utilities/routes';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'auth/context/AuthContext';
import { twMerge } from 'tailwind-merge';
import {
  GetPatientOutput,
  ProductOutput,
  ProviderOutput,
  useUpdateCartProductsProviderMutation,
} from 'graphql/generated/hasura';
import { ProductCategory } from '../../../superadmin/pages/products/list/useGetTableConfig';
import { MAX_QUANTITY, MIN_QUANTITY } from 'hooks/useShopCart';
import { useProviderConnect } from '../../../../router/patient/ConectProviderContext';
import AlertComponent from '../../../../components/alertComponent';

export type PatientOutput = Pick<
  GetPatientOutput,
  'id' | 'parentCodexId' | 'SENSITIVE_firstname' | 'SENSITIVE_lastname'
>;

export type Product = ProductOutput & {
  cartProductId: string;
  quantity: number;
};

interface ProductByUserProps {
  patientCodexId: string;
  provider?: Option;
  patientOwner?: PatientOutput | null;
  products: Product[];
  isLoadingRequests: boolean;
  updateSelectedProducts: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  updateIsLoadingCartMutation: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ProductRowProps {
  id: number;
  image_url?: string;
  shortTitle?: string;
  price: number;
  updateSelectedProducts: Dispatch<SetStateAction<Record<string, number>>>;
  currentQuantity: number;
  updateIsLoading: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
}

const ProductRow: React.FC<ProductRowProps> = ({
  image_url,
  shortTitle,
  price,
  updateSelectedProducts,
  currentQuantity,
  updateIsLoading,
  isLoading,
  id,
}) => {
  const [imageFailed, setImageFailed] = useState(false);

  const handleError = () => {
    if (image_url) {
      setImageFailed(true);
    }
  };

  return (
    <div className="px-5 py-4 rounded-10 bg-white border border-gray-opacity-15 gap-1.5 flex flex-col desktop:grid desktop:grid-cols-12 items-center">
      <div className="flex col-span-9 justify-start w-full items-center gap-2.5">
        <div className="desktop:w-11 desktop:h-11 w-16 h-16 overflow-hidden border border-gray-opacity-15 rounded-10 desktop:border-transparent min-w-[44px] min-h-[44px]">
          {imageFailed || !image_url ? (
            <div className="flex items-center justify-center w-full h-full bg-white">
              <DefaultTest className="fill-charcoal-gray stroke-charcoal-gray" />
            </div>
          ) : (
            <img
              className={twMerge('object-cover object-center w-full h-full')}
              onError={handleError}
              src={`${image_url}?preview=true`}
              alt="Test Photo"
            />
          )}
        </div>
        <p className="font-semibold text-h4 w-full text-neutral-800">
          {shortTitle}
        </p>
      </div>
      <div className="flex col-span-2 flex-row-reverse desktop:flex-row items-center gap-2.5 justify-between w-full">
        <EditQuantityCell
          currentQuantity={currentQuantity}
          increase={() => {
            updateIsLoading(true);
            updateSelectedProducts((prevState) => {
              return {
                ...prevState,
                [id]: Math.min((prevState[id] ?? 1) + 1, MAX_QUANTITY),
              };
            });
          }}
          decrease={() => {
            updateIsLoading(true);
            updateSelectedProducts((prevState) => {
              return {
                ...prevState,
                [id]: Math.max((prevState[id] ?? 0) - 1, MIN_QUANTITY),
              };
            });
          }}
          onChange={(amount) => {
            updateIsLoading(true);
            updateSelectedProducts((prevState) => {
              return {
                ...prevState,
                [id]: amount,
              };
            });
          }}
          withTrashIcon
          isLoading={isLoading}
          containerClassName="desktop:w-[240px] desktop:mx-auto"
        />
        <p className="font-semibol desktop:hidden text-h4 text-neutral-800">
          ${price * currentQuantity}
        </p>
      </div>
      <p className="font-semibol ml-auto max-desktop:hidden col-span-1 text-h4 text-neutral-800">
        ${price * currentQuantity}
      </p>
    </div>
  );
};

const converToSelect = (provider: ProviderOutput) => {
  const { id, SENSITIVE_firstname, SENSITIVE_lastname } = provider;
  return {
    label: `${SENSITIVE_firstname} ${SENSITIVE_lastname}`,
    value: id,
  };
};

const ProductByUser: React.FC<ProductByUserProps> = ({
  patientCodexId,
  provider,
  products,
  updateSelectedProducts,
  updateIsLoadingCartMutation,
  isLoadingRequests,
  patientOwner,
}) => {
  const { user, selectedAccount } = useAuth();
  const { openAgreementModal } = useProviderConnect();

  const [selectedProvider, updateSelectedProvider] = useState<
    Option | undefined
  >();

  const ownerName = `${patientOwner?.SENSITIVE_firstname} ${patientOwner?.SENSITIVE_lastname}`;

  const navigate = useNavigate();
  const [updateCartProductsProvider] = useUpdateCartProductsProviderMutation();

  const hasTests = !!products?.filter(
    ({ category }) => category === ProductCategory.Test_Kits,
  ).length;

  const { data } = useGetProvidersByPatientIdQuery({
    fetchPolicy: 'network-only',
    variables: {
      patientCodexId: patientCodexId,
    },
    onError: (error) => {
      console.error('Error getting providers', error);
      return;
    },
    onCompleted: (data) => {
      const providers = data.getProvidersByPatientId?.providers ?? [];
      const signetAgreementProviders = providers.filter(({ patientRequests }) =>
        (patientRequests ?? [])
          .map(({ requestStatus }) => requestStatus)
          .includes(ProviderRequestStatus.Accepted),
      ) as ProviderOutput[];

      updateSelectedProvider(
        signetAgreementProviders.map(({ id }) => id).includes(provider?.value)
          ? provider
          : converToSelect(signetAgreementProviders[0]),
      );

      if (!provider?.value && hasTests) {
        updateCartProductsProvider({
          variables: {
            providerCodexId: providersList[0]?.value,
            cartProductIds: products?.map(
              ({ cartProductId }) => +cartProductId,
            ),
          },
        });
      }
    },
  });

  const providers = useMemo(
    () => data?.getProvidersByPatientId?.providers ?? [],
    [data],
  );
  const providersList = providers.map((p) =>
    converToSelect(p as ProviderOutput),
  );
  const signetAgreementProviders = providers.filter(({ patientRequests }) =>
    (patientRequests ?? [])
      .map(({ requestStatus }) => requestStatus)
      .includes(ProviderRequestStatus.Accepted),
  ) as ProviderOutput[];

  const [alertProvider, setAlertProvider] = useState<string | null>(null);

  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.SHOP_DIAGNOSTIC,
  });

  const goToSelectProvider = () => {
    navigate(PROVIDERS, {
      state: {
        isFromShoppingCart: true,
      },
    });
  };

  return (
    <div className="border-b border-b-gray-opacity-15 pb-30 last:border-none last:pb-0">
      <AlertComponent
        text={`Looks like this patient does not have a signed agreement with ${alertProvider} provider. Please resolve it by switching over to correspondent account and signing the data agreement, or select another provider.`}
        type="warning"
        isNotification
        enableAutoFittingOfExtraActions
        hideCloseButton={false}
        onClose={() => setAlertProvider(null)}
        desktopItemsAlign={twMerge(
          'hidden desktop:items-center',
          alertProvider && 'block',
        )}
      />
      <div className="bg-white rounded-10 p-5 w-full flex flex-col desktop:flex-row desktop:gap-30 gap-5 mb-2 mt-4">
        <div
          className={twMerge(
            'text-med-gray-3 flex flex-col gap-2',
            selectedAccount !== patientOwner?.id &&
              user?.uuid !== patientOwner?.id &&
              'hidden',
          )}
        >
          <p className="text-sm">
            Please select a health care provider who will receive your results
            and and counsel you on the meaning of your results. You will need to
            book an appointment with this provider once your tests results are
            ready.
          </p>
        </div>
        <div
          className={twMerge(
            'text-med-gray-3 flex flex-col gap-2',
            (selectedAccount === patientOwner?.id ||
              user?.uuid === patientOwner?.id) &&
              'hidden',
          )}
        >
          <p className={'text-base font-semibold'}>
            <span className="text-med-gray-3">For child: </span>
            <span className="text-neutral-800">{ownerName}</span>
          </p>
          <p className="text-sm">
            Your child’s healthcare provider who will receive the test kit
            results. Book an appointment for your child once the results are
            available.
          </p>
        </div>
        <div
          className={twMerge(
            'flex flex-col desktop:flex-row gap-2.5 items-center',
            !hasTests && 'hidden',
          )}
        >
          <div className="flex items-start desktop:items-center gap-0.5 desktop:gap-4 w-full desktop:w-fit flex-col desktop:flex-row">
            <label className="font-semibold text-btn-black hidden desktop:block">
              {locale?.provider}
            </label>
            <Select
              className="dropdown desktop:max-w-[200px] desktop:w-[200px] w-full"
              classNamePrefix="react-select"
              value={selectedProvider}
              options={providersList}
              onChange={(option) => {
                const isCurrentAccount =
                  patientCodexId === (selectedAccount || user?.uuid);
                const signedProviderIds = signetAgreementProviders.map(
                  ({ id }) => id,
                );
                const isUnagreementProvider = !signedProviderIds.includes(
                  option?.value,
                );
                if (isUnagreementProvider && isCurrentAccount) {
                  return openAgreementModal(option?.value);
                }
                if (isUnagreementProvider && !isCurrentAccount) {
                  return setAlertProvider(option?.label as string);
                }
                updateSelectedProvider(option as Option);
                setAlertProvider(null);
                updateCartProductsProvider({
                  variables: {
                    providerCodexId: option?.value ?? '',
                    cartProductIds: products?.map(
                      ({ cartProductId }) => +cartProductId,
                    ),
                  },
                });
              }}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>

          <ButtonComponent
            className={twMerge(
              'h-fit !text-sm',
              selectedAccount !== patientOwner?.id &&
                user?.uuid !== patientOwner?.id &&
                'hidden',
            )}
            testID="select-new-provider-button"
            type="outlined"
            onClick={goToSelectProvider}
          >
            <div className="whitespace-nowrap">Select New Provider</div>
          </ButtonComponent>
        </div>
      </div>
      <div
        className={twMerge(
          'flex flex-col gap-2.5',
          !products?.length && 'hidden',
        )}
      >
        {products?.map(({ cartProductId, quantity, ...product }) => (
          <ProductRow
            image_url={product?.image_url ?? ''}
            id={+cartProductId}
            shortTitle={product?.title}
            price={product?.price ?? 0}
            currentQuantity={quantity}
            updateSelectedProducts={updateSelectedProducts}
            updateIsLoading={updateIsLoadingCartMutation}
            isLoading={isLoadingRequests}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductByUser;
